import React, { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import UserSettingsAccount from './UserSettingsAccount'
import UserSettingsProfile from './UserSettingsProfile'
import UserSettingsPrivacy from './UserSettingsPrivacy'
import UserSettingsNotifications from './UserSettingsNotifications'
import UserSettingsAdBreaks from './UserSettingsAdBreaks'
import ScrollableTabsList from 'ui/ScrollableTabsList'
import { getStoryUiStrings, resolveStoryLocale } from 'i18n/storyUi'
import {
    getUserSettingsUiStrings,
    resolveUserSettingsLocale,
} from 'i18n/userSettingsUi'
import RequireAuth from 'components/Auth/RequireAuth'

const UserSettings: React.FC = () => {
    const { tab } = useParams<{ tab: string }>()
    const [selectedTab, setSelectedTab] = useState<string>(tab || 'profile')
    const storyUi = useMemo(() => getStoryUiStrings(resolveStoryLocale()), [])
    const settingsUi = useMemo(
        () => getUserSettingsUiStrings(resolveUserSettingsLocale()),
        []
    )

    // Keep the active tab in sync with the URL so deep links (e.g.
    // `/settings/privacy`) land on the right panel.
    useEffect(() => {
        if (tab) {
            setSelectedTab(tab)
        } else {
            setSelectedTab('profile')
        }
    }, [tab])

    const tabsData = useMemo(
        () => [
            {
                label: settingsUi.tabProfile,
                route: '/settings/profile',
                value: 'profile',
            },
            {
                label: settingsUi.tabPrivacy,
                route: '/settings/privacy',
                value: 'privacy',
            },
            {
                label: settingsUi.tabAccount,
                route: '/settings/account',
                value: 'account',
            },
            {
                label: storyUi.navAdBreak,
                route: '/settings/ad-breaks',
                value: 'ad-breaks',
            },
            {
                label: settingsUi.tabNotifications,
                route: '/settings/notifications',
                value: 'notifications',
            },
        ],
        [
            settingsUi.tabAccount,
            settingsUi.tabNotifications,
            settingsUi.tabPrivacy,
            settingsUi.tabProfile,
            storyUi.navAdBreak,
        ]
    )

    return (
        <RequireAuth loadingVariant="settings">
            <div className="max-w-4xl m-4">
                <h1 className="my-2 text-eoe-text-heading dark:text-eoe-dark-text-heading text-xl font-bold">
                    {settingsUi.pageTitle}
                </h1>

                {/* Tabs Navigation */}
                <div className="horizontal-tablist my-2">
                    <ScrollableTabsList
                        key="settings-tabs"
                        tabs={tabsData}
                        selectedTab={selectedTab}
                        onTabChange={() => {}}
                        pageTitle={settingsUi.pageTitle}
                    />
                </div>

                {/* Tabs Content */}
                <div className="">
                    {selectedTab === 'account' && (
                        <div
                            role="tabpanel"
                            id="account-panel"
                            aria-labelledby="account-tab"
                            className="focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-eoe-primary dark:focus-visible:ring-eoe-dark-primary"
                        >
                            <UserSettingsAccount />
                        </div>
                    )}
                    {selectedTab === 'profile' && (
                        <div
                            role="tabpanel"
                            id="profile-panel"
                            aria-labelledby="profile-tab"
                            className="focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-eoe-primary dark:focus-visible:ring-eoe-dark-primary"
                        >
                            <UserSettingsProfile />
                        </div>
                    )}
                    {selectedTab === 'privacy' && (
                        <div
                            role="tabpanel"
                            id="privacy-panel"
                            aria-labelledby="privacy-tab"
                            className="focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-eoe-primary dark:focus-visible:ring-eoe-dark-primary"
                        >
                            <UserSettingsPrivacy />
                        </div>
                    )}
                    {selectedTab === 'notifications' && (
                        <div
                            role="tabpanel"
                            id="notifications-panel"
                            aria-labelledby="notifications-tab"
                            className="focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-eoe-primary dark:focus-visible:ring-eoe-dark-primary"
                        >
                            <UserSettingsNotifications />
                        </div>
                    )}
                    {selectedTab === 'ad-breaks' && (
                        <div
                            role="tabpanel"
                            id="ad-breaks-panel"
                            aria-labelledby="ad-breaks-tab"
                            className="focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-eoe-primary dark:focus-visible:ring-eoe-dark-primary"
                        >
                            <UserSettingsAdBreaks />
                        </div>
                    )}
                </div>
            </div>
        </RequireAuth>
    )
}

export default UserSettings
